import { alphabeticBaselineY, symbolMapFor } from './pdp-preview-symbol.js';
// Every dependency — geometry, fonts, icon URLs, timers, fetch — arrives
// through the caller so this module stays blind to which PDP flow it serves and
// so the pure core is testable under node --test, without a browser.

export const PREVIEW_FRAME_RATIO = 1634 / 1915; // the shared cover-photo frame (see .pdp-preview)
export const PREVIEW_LONG_EDGE = 800;
// Fallback only. The backdrop composed into every captured preview lives in
// `--preview-frame-bg` (snippets/css-variables.liquid), because the same value has to
// paint every surface that later displays the WebP — otherwise the image reads as a
// rectangle floating on a lighter box. `resolveFrameBackground` reads the property so
// the stylesheet stays the single source; this constant only covers a caller with no
// document to read from (tests, or a stylesheet that failed to load).
export const PREVIEW_FRAME_BG = '#ebebeb';

export function resolveFrameBackground(readProperty) {
  const declared = readProperty ? readProperty('--preview-frame-bg') : '';
  return String(declared || '').trim() || PREVIEW_FRAME_BG;
}

export function previewSignature(state) {
  return JSON.stringify({
    color: state.colorLabel || '',
    image: state.imageSrc || '',
    foil: state.foilId || '',
    cover: (state.coverLines || []).map((line) => [line.text || '', line.icon || '']),
    side: [state.sideText || '', state.sideIcon || ''],
    fonts: [state.coverFontCqw || 0, state.spineFontCqw || 0],
  });
}

export function layoutPreviewLines({ width, height, geo, coverFontCqw, spineFontCqw, coverLines, sideText, sideIcon }) {
  const ops = [];
  const coverFontPx = (coverFontCqw / 100) * width;
  const gapPx = width / 100; // .pdp-preview__cover gap: 1cqw
  const lines = coverLines || [];
  const blockHeight = lines.length * coverFontPx + Math.max(0, lines.length - 1) * gapPx;
  const blockCenterY = (geo.cy / 100) * height;
  lines.forEach((line, index) => {
    ops.push({
      text: line.text || '', icon: line.icon || '',
      x: (geo.cx / 100) * width,
      y: blockCenterY - blockHeight / 2 + coverFontPx / 2 + index * (coverFontPx + gapPx),
      fontPx: coverFontPx,
    });
  });
  const side = (sideText || '').trim();
  if (side || sideIcon) {
    ops.push({
      text: side, icon: sideIcon || '',
      x: (geo.sx / 100) * width,
      y: (geo.sy / 100) * height,
      fontPx: (spineFontCqw / 100) * width,
    });
  }
  return ops;
}

// The frame is a fixed 800×683 box with the studio photo letterboxed inside it,
// so an album can occupy as little as 53% of the width — grey margins that then
// eat most of a cart thumbnail. Find what is actually drawn, so the upload
// carries the artwork instead of the padding.
//
// `pixels` is RGBA in row order (a canvas ImageData.data). The background colour
// is sampled rather than hardcoded — it is whatever renderPreviewCanvas filled
// with, and sampling keeps the two in step. It is read from the four corners and
// must agree on at least two: a single-corner read would mistake artwork for the
// background if the drawing happened to reach that corner, and silently return a
// crop of nothing. Corners that never agree mean the frame has no discernible
// background, so the caller is told to leave it alone.
// Returns null when nothing differs from the background too — an empty frame is
// left uncropped rather than cropped to nothing.
export function findContentBox({ pixels, width, height, tolerance = 18, marginRatio = 0.04 }) {
  if (!pixels || !width || !height) return null;
  const background = sampleBackground({ pixels, width, height, tolerance });
  if (!background) return null;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const differs = Math.abs(pixels[index] - background[0]) > tolerance
        || Math.abs(pixels[index + 1] - background[1]) > tolerance
        || Math.abs(pixels[index + 2] - background[2]) > tolerance;
      if (differs) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  // Breathing room proportional to the subject, then clamped to the frame — a
  // subject already touching an edge must not produce an out-of-bounds crop.
  const margin = Math.round(Math.max(maxX - minX, maxY - minY) * marginRatio);
  const left = Math.max(0, minX - margin);
  const top = Math.max(0, minY - margin);
  const right = Math.min(width - 1, maxX + margin);
  const bottom = Math.min(height - 1, maxY + margin);
  return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

// The frame's flat fill, taken from the four corners. The winner needs a STRICT
// majority — three of four — and it must be unique. Two corners is not enough: both
// 2-2 (artwork against fill) and 2-1-1 leave the pair as likely to be artwork that
// reached two corners as the backdrop, and choosing wrong inverts the whole scan, so
// the real background gets measured as content. Ambiguity returns null, which the
// caller reads as "leave this frame alone" rather than acting on a coin flip.
// In practice the real frames are unanimous: the photo is letterboxed inside the
// frame, so all four corners are fill.
export function sampleBackground({ pixels, width, height, tolerance = 18 }) {
  const corners = [
    [0, 0], [width - 1, 0], [0, height - 1], [width - 1, height - 1],
  ].map(([x, y]) => {
    const index = (y * width + x) * 4;
    return [pixels[index], pixels[index + 1], pixels[index + 2]];
  });
  const alike = (left, right) => left.every((channel, position) => Math.abs(channel - right[position]) <= tolerance);
  const tallied = corners.map((candidate) => ({
    candidate,
    votes: corners.filter((corner) => alike(candidate, corner)).length,
  }));
  const best = Math.max(...tallied.map((entry) => entry.votes));
  if (best < 3) return null;
  const winners = tallied.filter((entry) => entry.votes === best);
  // A tie can still be one colour counted from several of its own corners; only
  // distinct colours make it genuinely ambiguous.
  const distinct = winners.filter((entry, position) =>
    winners.findIndex((other) => alike(entry.candidate, other.candidate)) === position);
  return distinct.length === 1 ? distinct[0].candidate : null;
}

export function buildUploadForm(fields, blob) {
  // Presign contract: every returned field first (Content-Type among them,
  // before the file), the file part last, nothing rewritten — the key is an
  // equality condition inside the signed policy.
  const form = new FormData();
  Object.entries(fields || {}).forEach(([name, value]) => form.append(name, value));
  form.append('file', blob, 'preview.webp');
  return form;
}

export function createUploadScheduler({ debounceMs, perform, setTimer, clearTimer }) {
  const timerSet = setTimer || ((fn, ms) => setTimeout(fn, ms));
  const timerClear = clearTimer || ((id) => clearTimeout(id));
  const results = new Map(); // signature → publicUrl | null (null = tried and failed; no retry)
  // Keyed by signature, not a single slot: a buyer who toggles A → B → A while
  // A is still uploading must rejoin A's promise instead of starting a second
  // upload of the same pixels, and settle() must be able to await only the
  // signature it is settling.
  const inflight = new Map(); // signature → promise
  let latestSignature = null;
  let latestState = null;
  let timer = null;

  const start = (signature, state) => {
    if (results.has(signature)) return Promise.resolve(results.get(signature));
    const running = inflight.get(signature);
    if (running) return running;
    let performed;
    try {
      // Synchronous call on purpose: settle()'s flush must have the work
      // running before it awaits, or a caller releasing the perform in the
      // same tick would race a microtask-deferred start.
      performed = Promise.resolve(perform(state));
    } catch {
      performed = Promise.resolve(null);
    }
    const promise = performed
      .catch(() => null)
      .then((publicUrl) => {
        results.set(signature, publicUrl || null);
        inflight.delete(signature);
        return publicUrl || null;
      });
    inflight.set(signature, promise);
    return promise;
  };

  return {
    schedule(state) {
      latestState = state;
      latestSignature = previewSignature(state);
      if (results.has(latestSignature)) return;
      if (inflight.has(latestSignature)) return;
      if (timer != null) timerClear(timer);
      timer = timerSet(() => {
        timer = null;
        start(latestSignature, latestState);
      }, debounceMs);
    },
    current() {
      if (latestSignature == null) return null;
      return results.get(latestSignature) || null;
    },
    // Forget the latest state without dropping settled results: a cleared
    // personalization must stop answering with the previous state's URL, but
    // re-entering that exact state may reuse its cached upload.
    clear() {
      if (timer != null) {
        timerClear(timer);
        timer = null;
      }
      latestSignature = null;
      latestState = null;
    },
    async settle(timeoutMs) {
      if (timer != null) { // flush the debounce — the click IS the settling event
        timerClear(timer);
        timer = null;
        start(latestSignature, latestState);
      }
      const pending = latestSignature == null ? null : inflight.get(latestSignature);
      if (pending) {
        let timeoutId = null;
        await Promise.race([
          pending,
          new Promise((resolve) => { timeoutId = timerSet(resolve, timeoutMs); }),
        ]);
        // Drop the loser of the race: an uncancelled timer keeps a callback
        // alive for the full timeout after every add-to-cart.
        if (timeoutId != null) timerClear(timeoutId);
      }
      return this.current();
    },
  };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Shopify CDN sends Access-Control-Allow-Origin: *
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('preview image failed to load'));
    image.src = src;
  });
}

export const FOIL_CANVAS = {
  dourado: {
    stops: [
      [0, 'hsl(45 96% 72%)'],
      [0.28, 'hsl(43 98% 60%)'],
      [0.55, 'hsl(38 95% 52%)'],
      [0.78, 'hsl(33 92% 46%)'],
      [1, 'hsl(29 88% 40%)'],
    ],
    shadows: ['rgba(255, 240, 200, 0.55)', 'rgba(120, 55, 0, 0.5)'],
  },
  prata: {
    stops: [
      [0, 'hsl(0 0% 78%)'],
      [0.47, 'hsl(0 0% 90%)'],
      [0.53, 'hsl(0 0% 78%)'],
      [1, 'hsl(0 0% 70%)'],
    ],
    shadows: ['rgba(255, 255, 255, 0.4)', 'rgba(0, 0, 0, 0.55)'],
  },
};

function tintedIcon(iconImage, stops, size) {
  const side = Math.max(1, Math.ceil(size));
  const offscreen = document.createElement('canvas');
  offscreen.width = side;
  offscreen.height = side;
  const offscreenContext = offscreen.getContext('2d');
  offscreenContext.drawImage(iconImage, 0, 0, side, side);
  offscreenContext.globalCompositeOperation = 'source-in';
  const gradient = offscreenContext.createLinearGradient(0, 0, 0, side);
  stops.forEach(([offset, color]) => gradient.addColorStop(offset, color));
  offscreenContext.fillStyle = gradient;
  offscreenContext.fillRect(0, 0, side, side);
  return offscreen;
}

export async function renderPreviewCanvas({ imageSrc, foilId, geo, coverFontCqw, spineFontCqw, coverLines, sideText, sideIcon, iconMap, iconBoldMap }) {
  const frameWidth = PREVIEW_LONG_EDGE;
  const frameHeight = Math.round(PREVIEW_LONG_EDGE * PREVIEW_FRAME_RATIO);
  const canvas = document.createElement('canvas');
  canvas.width = frameWidth;
  canvas.height = frameHeight;
  const context = canvas.getContext('2d');

  context.fillStyle = resolveFrameBackground((name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name));
  context.fillRect(0, 0, frameWidth, frameHeight);

  if (imageSrc) {
    const baseImage = await loadImage(imageSrc);
    // object-fit: contain letterboxing — scale to the smaller ratio, centered,
    // so a shot that isn't exactly 1915:1634 still lands where the DOM puts it.
    const scale = Math.min(frameWidth / baseImage.naturalWidth, frameHeight / baseImage.naturalHeight);
    const drawWidth = baseImage.naturalWidth * scale;
    const drawHeight = baseImage.naturalHeight * scale;
    const drawX = (frameWidth - drawWidth) / 2;
    const drawY = (frameHeight - drawHeight) / 2;
    context.drawImage(baseImage, drawX, drawY, drawWidth, drawHeight);
  }

  const foil = FOIL_CANVAS[foilId] || FOIL_CANVAS.prata;
  const cqwToPx = frameWidth / 100;
  const ops = layoutPreviewLines({
    width: frameWidth, height: frameHeight, geo, coverFontCqw, spineFontCqw, coverLines, sideText, sideIcon,
  });

  const symbolMap = symbolMapFor(foilId, iconMap, iconBoldMap);
  const iconImages = new Map();
  const loadIcon = async (iconName) => {
    if (!symbolMap || !symbolMap[iconName]) return null;
    if (!iconImages.has(iconName)) {
      iconImages.set(iconName, await loadImage(symbolMap[iconName]));
    }
    return iconImages.get(iconName);
  };

  for (const op of ops) {
    // A missing symbol only drops the symbol — the engraving text must still
    // render rather than the whole capture failing over one icon fetch.
    const iconImage = op.icon ? await loadIcon(op.icon).catch(() => null) : null;
    const fontPx = op.fontPx;
    // A line with an icon pre-shifts the text left so the text+icon pair
    // stays centred on op.x, matching the inline-flow layout of the DOM.
    const iconSize = Math.max(1.1 * fontPx, 2 * cqwToPx);
    const textShift = iconImage ? (iconSize + 0.2 * fontPx) / 2 : 0;
    const textX = op.x - textShift;
    const shadowOffsetX = 0.048 * fontPx;
    const shadowOffsetYFirst = 0.064 * fontPx;
    const shadowOffsetYSecond = 0.072 * fontPx;

    context.font = `500 ${fontPx}px 'Times New Roman', Times, serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    const text = op.text.toUpperCase();

    context.fillStyle = foil.shadows[0];
    context.fillText(text, textX + shadowOffsetX, op.y + shadowOffsetYFirst);

    context.fillStyle = foil.shadows[1];
    context.fillText(text, textX - shadowOffsetX, op.y - shadowOffsetYSecond);

    const gradient = context.createLinearGradient(0, op.y - fontPx / 2, 0, op.y + fontPx / 2);
    foil.stops.forEach(([offset, color]) => gradient.addColorStop(offset, color));
    context.fillStyle = gradient;
    context.fillText(text, textX, op.y);

    if (iconImage) {
      const tinted = tintedIcon(iconImage, foil.stops, iconSize);
      const iconX = textX + context.measureText(text).width / 2 + 0.2 * fontPx;
      const iconY = alphabeticBaselineY(context, text, op.y, fontPx) - 0.331 * fontPx - iconSize / 2;
      const [lightShadow, darkShadow] = foil.shadows.map((color) => tintedIcon(iconImage, [[0, color], [1, color]], iconSize));
      context.drawImage(lightShadow, iconX + shadowOffsetX / 2, iconY + shadowOffsetYFirst / 2, iconSize, iconSize);
      context.drawImage(darkShadow, iconX - shadowOffsetX / 2, iconY - shadowOffsetYSecond / 2, iconSize, iconSize);
      context.drawImage(tinted, iconX, iconY, iconSize, iconSize);
    }
  }

  return cropToContent(canvas);
}

// Crop the finished frame down to what was drawn. Fails open in every direction:
// unreadable pixels (a tainted canvas), an empty frame, or a subject that already
// fills the frame all return the canvas untouched, because a preview with padding
// beats no preview at all.
export function cropToContent(canvas) {
  const context = canvas.getContext('2d');
  let pixels;
  try {
    pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  } catch {
    return canvas; // tainted canvas — ship the padded frame rather than nothing
  }
  const box = findContentBox({ pixels, width: canvas.width, height: canvas.height });
  if (!box || (box.width >= canvas.width && box.height >= canvas.height)) return canvas;

  const cropped = document.createElement('canvas');
  cropped.width = box.width;
  cropped.height = box.height;
  cropped.getContext('2d').drawImage(
    canvas, box.x, box.y, box.width, box.height, 0, 0, box.width, box.height,
  );
  return cropped;
}

export function toWebpBlob(canvas) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      // Safari has no WebP encoder — resolve null so the caller fails open
      // instead of uploading a mistyped blob the signed policy would 403.
      resolve(blob && blob.type === 'image/webp' ? blob : null);
    }, 'image/webp', 0.9);
  });
}

export function createPreviewCapture({ endpoint, geo, iconMap, iconBoldMap, debounceMs, fetchImpl }) {
  const fetchFn = fetchImpl || ((...args) => fetch(...args));
  if (!endpoint) {
    // Capture disabled (dev store / unknown env): inert facade, add-to-cart untouched.
    return { schedule() {}, clear() {}, current: () => null, settle: async () => null };
  }
  const perform = async (state) => {
    const canvas = await renderPreviewCanvas({ ...state, geo, iconMap, iconBoldMap });
    const blob = await toWebpBlob(canvas);
    if (!blob || blob.size < 1 || blob.size > 1048576) return null; // signed policy: 1 B – 1 MB
    // No body on purpose: the handler ignores it, and a bare POST needs no preflight.
    const presignResponse = await fetchFn(endpoint, { method: 'POST' });
    if (!presignResponse.ok) return null;
    const presign = await presignResponse.json();
    if (!presign || !presign.publicUrl || !presign.upload || !presign.upload.url) return null;
    const uploadResponse = await fetchFn(presign.upload.url, {
      method: 'POST',
      body: buildUploadForm(presign.upload.fields, blob),
    });
    if (!uploadResponse.ok) return null;
    return presign.publicUrl; // verbatim — the ingest drops any derived shape
  };
  return createUploadScheduler({ debounceMs: debounceMs ?? 1200, perform });
}

export function stateFromStore(storeState, options) {
  const colorIndex = storeState.colorIndex || 0;
  const images = options.colorImages || [];
  const lines = (storeState.coverText || '').split('\n');
  const icons = storeState.coverIcons || [];
  return {
    imageSrc: images[colorIndex] || images[0] || options.previewBase || '',
    colorLabel: ((options.colors || [])[colorIndex] || {}).label || '',
    foilId: storeState.foilId || 'prata',
    coverLines: lines
      // Text stays byte-identical to what the DOM Preview() renders — the
      // trim below is only the DOM's own emptiness filter, never a rewrite.
      .map((text, index) => ({ text: text || '', icon: icons[index] || '' }))
      .filter((line) => line.text.trim().length || line.icon),
    sideText: (storeState.sideText || '').trim(),
    sideIcon: storeState.sideIcon || '',
    coverFontCqw: options.coverFontFor(storeState.titleSize),
    spineFontCqw: options.spineFontFor(storeState.sideSize),
  };
}

/**
 * Subscribe `capture` to the configurator's shared store.
 *
 * Returns a `detach()` that removes the subscription. The store outlives every
 * render — it is module scope, not component scope — so a second attach without
 * detaching the first would leave both subscribers scheduling captures against
 * two different product configs. That is exactly what happens when the cart
 * drawer's edit panel mounts the configurator over the one the PDP already
 * armed, so the caller must be able to undo it.
 */
export function attachToStore({ store, capture, shouldCapture, colors, colorImages, previewBase, coverFontFor, spineFontFor }) {
  const options = { colors, colorImages, previewBase, coverFontFor, spineFontFor };
  const subscriber = (storeState) => {
    if (!shouldCapture(storeState)) {
      // A state that stopped qualifying (e.g. the buyer deleted every text)
      // must not keep answering with the previous capture's URL.
      capture.clear();
      return;
    }
    capture.schedule(stateFromStore(storeState, options));
  };
  store.subs.add(subscriber);
  return () => store.subs.delete(subscriber);
}
